import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABEL, DEPT_LIST } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, KeyRound, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  email: "", name: "", role: "staff", job_title: "",
  departments: [], is_active: true, password: "",
};

export default function PortalStaff() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); // create form
  const [resetting, setResetting] = useState(null); // user to reset password for
  const [newPass, setNewPass] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleDept = (u, d) => {
    const cur = u.departments || [];
    const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d];
    return next;
  };

  const create = async () => {
    try {
      await api.post("/users", editing);
      toast.success("User created");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const update = async (u, patch) => {
    try {
      await api.patch(`/users/${u.id}`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resetPw = async () => {
    try {
      await api.post(`/users/${resetting.id}/reset-password`, { password: newPass });
      toast.success("Password reset");
      setResetting(null); setNewPass("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-12" data-testid="portal-staff">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-1">Admin</div>
          <h1 className="font-heading font-light text-3xl lg:text-4xl tracking-tighter">Staff management</h1>
          <p className="text-stone mt-2 text-sm">Invite staff, assign departments and set roles.</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} data-testid="add-staff-btn" className="bg-ink text-white hover:bg-ink/90 rounded-none">
          <Plus size={14} className="mr-2" /> Invite staff
        </Button>
      </div>

      <div className="border border-line bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-stone">
            <tr className="border-b border-line">
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Role</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Departments</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Last login</th>
              <th className="text-left px-4 py-3">Active</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-line" data-testid={`staff-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{u.name || "—"}</div>
                  <div className="text-xs text-stone">{u.email}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <select
                    value={u.role}
                    onChange={(e) => update(u, { role: e.target.value })}
                    className="bg-white border border-line px-2 py-1 text-xs"
                    disabled={u.id === user.id}
                  >
                    {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {DEPT_LIST.map((d) => {
                      const selected = (u.departments || []).includes(d.key);
                      return (
                        <button
                          key={d.key}
                          onClick={() => update(u, { departments: toggleDept(u, d.key) })}
                          className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${selected ? "bg-ink text-white border-ink" : "border-line hover:border-ink"}`}
                          data-testid={`staff-dept-${u.id}-${d.key}`}
                        >{d.short}</button>
                      );
                    })}
                  </div>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell font-mono text-xs text-stone">
                  {u.last_login_at ? u.last_login_at.slice(0, 10) : "—"}
                </td>
                <td className="px-4 py-3">
                  <Switch checked={u.is_active} onCheckedChange={(v) => update(u, { is_active: v })} disabled={u.id === user.id} />
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button onClick={() => setResetting(u)} className="text-stone hover:text-ink" aria-label="Reset password"><KeyRound size={14} /></button>
                    {u.id !== user.id && (
                      <button onClick={() => deleteUser(u)} className="text-stone hover:text-destructive" aria-label="Delete"><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-heading font-light text-2xl">Invite staff member</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="invite-name" /></div>
              <div><Label>Email</Label><Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="invite-email" /></div>
              <div><Label>Job title</Label><Input value={editing.job_title} onChange={(e) => setEditing({ ...editing, job_title: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} className="w-full bg-white border border-line px-3 py-2 text-sm">
                  {Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Departments</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {DEPT_LIST.map((d) => (
                    <button
                      key={d.key} type="button"
                      onClick={() => setEditing({
                        ...editing,
                        departments: (editing.departments || []).includes(d.key)
                          ? editing.departments.filter((x) => x !== d.key)
                          : [...(editing.departments || []), d.key]
                      })}
                      className={`text-xs uppercase tracking-widest px-3 py-1.5 border ${(editing.departments || []).includes(d.key) ? "bg-ink text-white border-ink" : "border-line hover:border-ink"}`}
                    >{d.label}</button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Initial password</Label>
                <Input type="password" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} data-testid="invite-password" placeholder="Min. 8 characters" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-ink text-white hover:bg-ink/90 rounded-none" onClick={create} data-testid="invite-create">Create user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetting} onOpenChange={(v) => !v && setResetting(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading font-light text-2xl">Reset password</DialogTitle></DialogHeader>
          {resetting && (
            <>
              <div className="text-sm text-stone mb-2">Set a new password for <strong className="text-ink">{resetting.email}</strong></div>
              <Input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} placeholder="New password" data-testid="reset-pw-input" />
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setResetting(null); setNewPass(""); }}>Cancel</Button>
            <Button className="bg-ink text-white hover:bg-ink/90 rounded-none" onClick={resetPw} data-testid="reset-pw-confirm" disabled={!newPass}>Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
